import React, { useState } from 'react';
import styled from 'styled-components';
import { authService } from '../../services/authService';
import VipIcon from '../Icons/VipIcons';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { PrimaryButton } from '../common/Button';
import { AUTH } from '../../constants/staticTexts';

const EmailVerificationModal = ({ email, onClose }) => {
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleResend = async () => {
    setResending(true);
    setMessage('');
    setError('');

    try {
      await authService.resendVerificationEmail();
      setMessage(AUTH.VERIFY_EMAIL_SUCCESS);
    } catch (err) {
      setError(err.message || AUTH.VERIFY_EMAIL_FAILED);
    } finally {
      setResending(false);
    }
  };

  return (
    <Overlay>
      <Modal>
        <IconWrapper>
          <VipIcon name="Mail" size={60} />
        </IconWrapper>

        <Title>{AUTH.VERIFY_EMAIL_TITLE}</Title>
        <Description>
          {AUTH.VERIFY_EMAIL_SENT_TO} <strong>{email}</strong>
        </Description>
        <Description>
          {AUTH.VERIFY_EMAIL_CHECK_INBOX}
        </Description>

        {message && (
          <SuccessMessage>
            <VipIcon name="CheckmarkCircle" size={20} />
            {message}
          </SuccessMessage>
        )}

        {error && (
          <ErrorMessage>
            <VipIcon name="AlertIcon" size={20} />
            {error}
          </ErrorMessage>
        )}

        <ButtonGroup>
          <PrimaryButton onClick={handleResend} disabled={resending}>
            {resending ? (
              <>
                <LoadingSpinner size={16} color="white" />
                {AUTH.VERIFY_EMAIL_SENDING}
              </>
            ) : (
              <>
                {AUTH.VERIFY_EMAIL_RESEND}
              </>
            )}
          </PrimaryButton>
        </ButtonGroup>

        <HelpText>
          {AUTH.VERIFY_EMAIL_HELP}
        </HelpText>
      </Modal>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  color: ${props => props.theme.colors.primaryBlue};
  animation: bounce 1s ease-in-out infinite;

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 12px;

  strong {
    color: #667eea;
    font-weight: 600;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  background: ${props => `${props.theme.colors.success}15`};
  color: ${props => props.theme.colors.darkGreen};
  padding: 12px 16px;
  border-radius: 8px;
  margin: 16px 0;
  font-size: 14px;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  background: ${props => `${props.theme.colors.error}15`};
  color: ${props => props.theme.colors.error};
  padding: 12px 16px;
  border-radius: 8px;
  margin: 16px 0;
  font-size: 14px;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: center;
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #999;
  margin-top: 20px;
  line-height: 1.5;
`;

export default EmailVerificationModal;
